import dayjs from "dayjs";

const PAGE_BACKGROUND = "#F7F8F9";
const TITLE_COLOR = "#262A2A";
const MUTED_COLOR = "#6F6F6F";
const FOOTER_COLOR = "#9A9A9A";
const PAGE_MARGIN = 10;
const FOOTER_SPACE = 8;
const SECTION_GAP = 2;
const CAPTURE_SCALE = 2;

const BASE_CAPTURE_OVERRIDES = `
  .card,
  .shadow,
  .shadow-sm,
  .btn {
    box-shadow: none !important;
  }
`;

const IGNORED_CLASSES = [
  "dropdown-menu",
  "daterangepicker",
  "apexcharts-tooltip",
  "apexcharts-xaxistooltip",
  "apexcharts-yaxistooltip",
  "pdf-ignore",
];

interface ExportDashboardPdfOptions {
  container: HTMLElement;
  sectionSelector: string;
  title: string;
  filtersText: string;
  fileName: string;
  captureCss?: string;
  onClone?: (clonedContainer: HTMLElement) => void;
}

const cropCanvas = (source: HTMLCanvasElement, top: number, height: number) => {
  const canvas = document.createElement("canvas");
  canvas.width = source.width;
  canvas.height = Math.max(1, Math.round(height));
  const context = canvas.getContext("2d");
  if (context) {
    context.fillStyle = PAGE_BACKGROUND;
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(source, 0, Math.round(top), source.width, canvas.height, 0, 0, canvas.width, canvas.height);
  }
  return canvas;
};

export const exportDashboardPdf = async ({
  container,
  sectionSelector,
  title,
  filtersText,
  fileName,
  captureCss = "",
  onClone,
}: ExportDashboardPdfOptions) => {
  const [{ default: html2canvas }, { jsPDF }] = await Promise.all([import("html2canvas"), import("jspdf")]);

  const sections = Array.from(container.querySelectorAll<HTMLElement>(sectionSelector));
  const containerRect = container.getBoundingClientRect();
  const snapshot = await html2canvas(container, {
    scale: CAPTURE_SCALE,
    backgroundColor: PAGE_BACKGROUND,
    useCORS: true,
    logging: false,
    windowWidth: document.documentElement.clientWidth,
    ignoreElements: (element) => IGNORED_CLASSES.some((className) => element.classList?.contains(className)),
    onclone: (clonedDocument, clonedContainer) => {
      const style = clonedDocument.createElement("style");
      style.textContent = `${BASE_CAPTURE_OVERRIDES}
        ${sectionSelector} {
          opacity: 1 !important;
        }
        ${captureCss}`;
      clonedDocument.head.appendChild(style);
      onClone?.(clonedContainer);
    },
  });
  const pixelRatio = snapshot.width / containerRect.width;

  const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4", compress: true });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const contentWidth = pageWidth - PAGE_MARGIN * 2;
  const contentBottom = pageHeight - FOOTER_SPACE;

  const paintPage = () => {
    pdf.setFillColor(PAGE_BACKGROUND);
    pdf.rect(0, 0, pageWidth, pageHeight, "F");
  };

  paintPage();
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(16);
  pdf.setTextColor(TITLE_COLOR);
  pdf.text(title, PAGE_MARGIN, PAGE_MARGIN + 6);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(9);
  pdf.setTextColor(MUTED_COLOR);
  pdf.text(`Generated on ${dayjs().format("DD MMM YYYY, hh:mm A")}`, pageWidth - PAGE_MARGIN, PAGE_MARGIN + 6, {
    align: "right",
  });
  const filterLines = pdf.splitTextToSize(filtersText, contentWidth) as string[];
  pdf.text(filterLines, PAGE_MARGIN, PAGE_MARGIN + 12);
  let cursorY = PAGE_MARGIN + 12 + filterLines.length * 4 + 2;

  sections.forEach((section) => {
    const rect = section.getBoundingClientRect();
    if (rect.height <= 0) return;
    const image = cropCanvas(snapshot, (rect.top - containerRect.top) * pixelRatio, rect.height * pixelRatio);

    let width = contentWidth;
    let height = (image.height * width) / image.width;
    const maxHeight = contentBottom - PAGE_MARGIN;
    if (height > maxHeight) {
      height = maxHeight;
      width = (image.width * height) / image.height;
    }
    if (cursorY + height > contentBottom) {
      pdf.addPage();
      paintPage();
      cursorY = PAGE_MARGIN;
    }
    pdf.addImage(
      image.toDataURL("image/jpeg", 0.92),
      "JPEG",
      PAGE_MARGIN + (contentWidth - width) / 2,
      cursorY,
      width,
      height,
      undefined,
      "FAST"
    );
    cursorY += height + SECTION_GAP;
  });

  const pageCount = pdf.getNumberOfPages();
  for (let page = 1; page <= pageCount; page += 1) {
    pdf.setPage(page);
    pdf.setFontSize(8);
    pdf.setTextColor(FOOTER_COLOR);
    pdf.text(title, PAGE_MARGIN, pageHeight - 4);
    pdf.text(`Page ${page} of ${pageCount}`, pageWidth - PAGE_MARGIN, pageHeight - 4, { align: "right" });
  }

  pdf.save(fileName);
};
